import React, { useState } from 'react';
import { 
  Palette, Globe, Key, Lock, Database, Activity, Zap, Workflow,
  Eye, EyeOff, Copy, Save, RefreshCw, ExternalLink, Bell, Mail,
  Shield, AlertTriangle, Check, Info, Upload, Download,
  Code, Cloud, Settings
} from 'lucide-react';
import { toast } from 'react-hot-toast';

// Branding & Domains Section
export const BrandingDomains = () => {
  const [showApiKey, setShowApiKey] = useState({});

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="space-y-6">
      {/* Domain Configuration */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Domain Configuration</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Platform Base Domain
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value="junotalent.com"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                placeholder="your-platform.com"
              />
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Update
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Tenants will get subdomains like: tenant.junotalent.com
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SSL Certificate Management
            </label>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Check className="h-5 w-5 text-green-600" />
                <span className="text-sm">Auto SSL (Let's Encrypt)</span>
              </div>
              <button className="px-3 py-1 text-sm bg-green-100 text-green-800 rounded hover:bg-green-200">
                Enabled
              </button>
            </div>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-3">Custom Domain Settings</h3>
            <div className="space-y-3">
              <label className="flex items-center space-x-2">
                <input type="checkbox" checked className="rounded" />
                <span className="text-sm">Allow custom domains per tenant</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" checked className="rounded" />
                <span className="text-sm">Automatic SSL for custom domains</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="rounded" />
                <span className="text-sm">Require domain verification</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Branding Configuration */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Branding Configuration</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Global Branding */}
          <div>
            <h3 className="font-medium text-gray-900 mb-4">Global Platform Branding</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Platform Name
                </label>
                <input
                  type="text"
                  value="Juno Talent Assist Platform"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Platform Logo
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <div className="text-center">
                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Upload logo (PNG, SVG)</p>
                    <p className="text-xs text-gray-500">Recommended: 200x50px</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Color
                </label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value="#3B82F6"
                    className="w-16 h-10 border border-gray-300 rounded"
                  />
                  <input
                    type="text"
                    value="#3B82F6"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Tenant Branding Options */}
          <div>
            <h3 className="font-medium text-gray-900 mb-4">Tenant Branding Options</h3>
            <div className="space-y-4">
              <label className="flex items-center space-x-2">
                <input type="checkbox" checked className="rounded" />
                <span className="text-sm">Allow custom logos per tenant</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" checked className="rounded" />
                <span className="text-sm">Allow custom color schemes</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="rounded" />
                <span className="text-sm">Allow custom CSS (Enterprise)</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="rounded" />
                <span className="text-sm">White-label mode (hide platform branding)</span>
              </label>

              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Branding Features by Plan</h4>
                <div className="text-xs space-y-1">
                  <div>• Starter: Basic logo upload</div>
                  <div>• Professional: Logo + colors</div>
                  <div>• Enterprise: Full white-label</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Global Integrations Section
export const GlobalIntegrations = ({ integrationConfig, setIntegrationConfig }) => {
  const [showApiKey, setShowApiKey] = useState({});

  const toggleApiKeyVisibility = (service) => {
    setShowApiKey(prev => ({ ...prev, [service]: !prev[service] }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const integrationCategories = [
    {
      title: 'AI & Machine Learning',
      integrations: [
        { key: 'openai', name: 'OpenAI', icon: '🤖', description: 'GPT models for job matching and content generation' },
        { key: 'anthropic', name: 'Anthropic Claude', icon: '🧠', description: 'Advanced reasoning and analysis' },
        { key: 'gemini', name: 'Google Gemini', icon: '💎', description: 'Multimodal AI capabilities' }
      ]
    },
    {
      title: 'Payment & Billing',
      integrations: [
        { key: 'stripe', name: 'Stripe', icon: '💳', description: 'Payment processing and subscription management' }
      ]
    },
    {
      title: 'Communication',
      integrations: [
        { key: 'sendgrid', name: 'SendGrid', icon: '📧', description: 'Email delivery service' },
        { key: 'slack', name: 'Slack', icon: '💬', description: 'Team communication and notifications' }
      ]
    },
    {
      title: 'Job Boards & Platforms',
      integrations: [
        { key: 'linkedin', name: 'LinkedIn', icon: '💼', description: 'Professional networking and recruiting' },
        { key: 'indeed', name: 'Indeed', icon: '🔍', description: 'Job posting and candidate sourcing' }
      ]
    },
    {
      title: 'Enterprise Systems',
      integrations: [
        { key: 'workday', name: 'Workday', icon: '🏢', description: 'HR and workforce management' },
        { key: 'servicenow', name: 'ServiceNow', icon: '⚙️', description: 'IT service management' }
      ]
    }
  ];

  return (
    <div className="space-y-6">
      {integrationCategories.map((category) => (
        <div key={category.title} className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{category.title}</h3>
          
          <div className="space-y-4">
            {category.integrations.map((integration) => {
              const config = integrationConfig[integration.key] || {};
              
              return (
                <div key={integration.key} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{integration.icon}</span>
                      <div>
                        <h4 className="font-medium text-gray-900">{integration.name}</h4>
                        <p className="text-sm text-gray-600">{integration.description}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        config.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {config.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <button
                        onClick={() => setIntegrationConfig({
                          ...integrationConfig,
                          [integration.key]: { ...config, enabled: !config.enabled }
                        })}
                        className={`px-3 py-1 rounded text-sm ${
                          config.enabled 
                            ? 'bg-red-100 text-red-800 hover:bg-red-200'
                            : 'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                      >
                        {config.enabled ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </div>

                  {config.enabled && (
                    <div className="mt-4 space-y-3 border-t pt-4">
                      {/* API Key field */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          API Key
                        </label>
                        <div className="flex space-x-2">
                          <div className="flex-1 relative">
                            <input
                              type={showApiKey[integration.key] ? 'text' : 'password'}
                              value={config.apiKey || ''}
                              onChange={(e) => setIntegrationConfig({
                                ...integrationConfig,
                                [integration.key]: { ...config, apiKey: e.target.value }
                              })}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10"
                              placeholder="Enter API key"
                            />
                            <button
                              type="button"
                              onClick={() => toggleApiKeyVisibility(integration.key)}
                              className="absolute right-2 top-1/2 transform -translate-y-1/2"
                            >
                              {showApiKey[integration.key] ? 
                                <EyeOff className="h-4 w-4 text-gray-400" /> : 
                                <Eye className="h-4 w-4 text-gray-400" />
                              }
                            </button>
                          </div>
                          {config.apiKey && (
                            <button
                              onClick={() => copyToClipboard(config.apiKey)}
                              className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Additional configuration fields based on integration */}
                      {integration.key === 'stripe' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Publishable Key
                          </label>
                          <input
                            type="text"
                            value={config.publishableKey || ''}
                            onChange={(e) => setIntegrationConfig({
                              ...integrationConfig,
                              [integration.key]: { ...config, publishableKey: e.target.value }
                            })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                            placeholder="pk_live_..."
                          />
                        </div>
                      )}

                      {(integration.key === 'openai' || integration.key === 'anthropic' || integration.key === 'gemini') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Default Model
                          </label>
                          <select
                            value={config.model || ''}
                            onChange={(e) => setIntegrationConfig({
                              ...integrationConfig,
                              [integration.key]: { ...config, model: e.target.value }
                            })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                          >
                            {integration.key === 'openai' && (
                              <>
                                <option value="gpt-4o">GPT-4o</option>
                                <option value="gpt-5">GPT-5</option>
                              </>
                            )}
                            {integration.key === 'anthropic' && (
                              <>
                                <option value="claude-3-sonnet">Claude 3 Sonnet</option>
                                <option value="claude-4-sonnet">Claude 4 Sonnet</option>
                              </>
                            )}
                            {integration.key === 'gemini' && (
                              <>
                                <option value="gemini-pro">Gemini Pro</option>
                                <option value="gemini-2.5-pro">Gemini 2.5 Pro</option>
                              </>
                            )}
                          </select>
                        </div>
                      )}

                      {integration.key === 'sendgrid' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            From Email
                          </label>
                          <input
                            type="email"
                            value={config.fromEmail || ''}
                            onChange={(e) => setIntegrationConfig({
                              ...integrationConfig,
                              [integration.key]: { ...config, fromEmail: e.target.value }
                            })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                            placeholder="noreply@yourplatform.com"
                          />
                        </div>
                      )}

                      {/* Test Connection Button */}
                      <div className="flex justify-end">
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                          Test Connection
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Global Integration Settings */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Global Integration Settings</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Allow Tenant-Specific Keys</h4>
              <p className="text-sm text-gray-600">Let tenants override with their own API keys</p>
            </div>
            <input type="checkbox" checked className="rounded" />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Rate Limiting</h4>
              <p className="text-sm text-gray-600">Apply API rate limits per tenant</p>
            </div>
            <input type="checkbox" checked className="rounded" />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Usage Monitoring</h4>
              <p className="text-sm text-gray-600">Track API usage and costs per tenant</p>
            </div>
            <input type="checkbox" checked className="rounded" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Security & Privacy Section
export const SecurityPrivacy = ({ securityConfig, setSecurityConfig }) => (
  <div className="space-y-6">
    {/* Password Policy */}
    <div className="bg-white rounded-xl shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Password Policy</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum Length
            </label>
            <input
              type="number"
              value={securityConfig.passwordPolicy.minLength}
              onChange={(e) => setSecurityConfig({
                ...securityConfig,
                passwordPolicy: { 
                  ...securityConfig.passwordPolicy, 
                  minLength: parseInt(e.target.value) 
                }
              })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              min="6"
              max="20"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password Expiry (Days)
            </label>
            <input
              type="number"
              value={securityConfig.passwordPolicy.maxAge}
              onChange={(e) => setSecurityConfig({
                ...securityConfig,
                passwordPolicy: { 
                  ...securityConfig.passwordPolicy, 
                  maxAge: parseInt(e.target.value) 
                }
              })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              min="30"
              max="365"
            />
          </div>
        </div>

        <div className="space-y-3">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={securityConfig.passwordPolicy.requireUppercase}
              onChange={(e) => setSecurityConfig({
                ...securityConfig,
                passwordPolicy: { 
                  ...securityConfig.passwordPolicy, 
                  requireUppercase: e.target.checked 
                }
              })}
              className="rounded"
            />
            <span className="text-sm">Require uppercase letters</span>
          </label>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={securityConfig.passwordPolicy.requireLowercase}
              onChange={(e) => setSecurityConfig({
                ...securityConfig,
                passwordPolicy: { 
                  ...securityConfig.passwordPolicy, 
                  requireLowercase: e.target.checked 
                }
              })}
              className="rounded"
            />
            <span className="text-sm">Require lowercase letters</span>
          </label>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={securityConfig.passwordPolicy.requireNumbers}
              onChange={(e) => setSecurityConfig({
                ...securityConfig,
                passwordPolicy: { 
                  ...securityConfig.passwordPolicy, 
                  requireNumbers: e.target.checked 
                }
              })}
              className="rounded"
            />
            <span className="text-sm">Require numbers</span>
          </label>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={securityConfig.passwordPolicy.requireSymbols}
              onChange={(e) => setSecurityConfig({
                ...securityConfig,
                passwordPolicy: { 
                  ...securityConfig.passwordPolicy, 
                  requireSymbols: e.target.checked 
                }
              })}
              className="rounded"
            />
            <span className="text-sm">Require symbols</span>
          </label>
        </div>
      </div>
    </div>

    {/* Data Protection */}
    <div className="bg-white rounded-xl shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Protection & Privacy</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Encryption</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm">Data at Rest Encryption</span>
              <span className={`px-2 py-1 rounded-full text-xs ${
                securityConfig.dataEncryption.enabled 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {securityConfig.dataEncryption.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm">Encryption Algorithm</span>
              <span className="text-sm font-medium">{securityConfig.dataEncryption.algorithm}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm">Key Rotation (Days)</span>
              <input
                type="number"
                value={securityConfig.dataEncryption.keyRotation}
                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                min="1"
                max="365"
              />
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-medium text-gray-900 mb-3">Privacy Compliance</h4>
          <div className="space-y-3">
            <label className="flex items-center space-x-2">
              <input type="checkbox" checked className="rounded" />
              <span className="text-sm">GDPR Compliance</span>
            </label>
            <label className="flex items-center space-x-2">
              <input type="checkbox" checked className="rounded" />
              <span className="text-sm">CCPA Compliance</span>
            </label>
            <label className="flex items-center space-x-2">
              <input type="checkbox" className="rounded" />
              <span className="text-sm">HIPAA Compliance</span>
            </label>
            <label className="flex items-center space-x-2">
              <input type="checkbox" checked className="rounded" />
              <span className="text-sm">Data Anonymization</span>
            </label>
          </div>
        </div>
      </div>
    </div>

    {/* Audit & Monitoring */}
    <div className="bg-white rounded-xl shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Audit & Monitoring</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Audit Logging</h4>
          <div className="space-y-3">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={securityConfig.auditLogging.enabled}
                onChange={(e) => setSecurityConfig({
                  ...securityConfig,
                  auditLogging: { 
                    ...securityConfig.auditLogging, 
                    enabled: e.target.checked 
                  }
                })}
                className="rounded"
              />
              <span className="text-sm">Enable audit logging</span>
            </label>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Log Retention (Days)
              </label>
              <input
                type="number"
                value={securityConfig.auditLogging.retentionDays}
                onChange={(e) => setSecurityConfig({
                  ...securityConfig,
                  auditLogging: { 
                    ...securityConfig.auditLogging, 
                    retentionDays: parseInt(e.target.value) 
                  }
                })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                min="30"
                max="2555" // 7 years
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Log Level
              </label>
              <select
                value={securityConfig.auditLogging.logLevel}
                onChange={(e) => setSecurityConfig({
                  ...securityConfig,
                  auditLogging: { 
                    ...securityConfig.auditLogging, 
                    logLevel: e.target.value 
                  }
                })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="ERROR">ERROR</option>
                <option value="WARN">WARN</option>
                <option value="INFO">INFO</option>
                <option value="DEBUG">DEBUG</option>
              </select>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-medium text-gray-900 mb-3">Security Monitoring</h4>
          <div className="space-y-3">
            <label className="flex items-center space-x-2">
              <input type="checkbox" checked className="rounded" />
              <span className="text-sm">Failed login attempts monitoring</span>
            </label>
            <label className="flex items-center space-x-2">
              <input type="checkbox" checked className="rounded" />
              <span className="text-sm">Suspicious activity detection</span>
            </label>
            <label className="flex items-center space-x-2">
              <input type="checkbox" className="rounded" />
              <span className="text-sm">Real-time security alerts</span>
            </label>
            <label className="flex items-center space-x-2">
              <input type="checkbox" checked className="rounded" />
              <span className="text-sm">Data export/download logging</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Database Configuration Section
export const DatabaseConfiguration = ({ systemConfig, setSystemConfig }) => (
  <div className="space-y-6">
    <div className="bg-white rounded-xl shadow-md p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">Database Architecture</h2>
      
      <div className="space-y-6">
        {/* Database Mode Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Multi-Tenant Database Strategy
          </label>
          <div className="space-y-3">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="databaseMode"
                value="shared"
                checked={systemConfig.databaseMode === 'shared'}
                onChange={(e) => setSystemConfig({...systemConfig, databaseMode: e.target.value})}
                className="mt-0.5"
              />
              <div>
                <div className="font-medium">Shared Database</div>
                <div className="text-sm text-gray-600">
                  All tenants share the same database with tenant isolation via tenant_id columns
                </div>
                <div className="text-xs text-green-600 mt-1">✓ Cost effective, easy maintenance</div>
              </div>
            </label>

            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="databaseMode"
                value="isolated"
                checked={systemConfig.databaseMode === 'isolated'}
                onChange={(e) => setSystemConfig({...systemConfig, databaseMode: e.target.value})}
                className="mt-0.5"
              />
              <div>
                <div className="font-medium">Database Per Tenant</div>
                <div className="text-sm text-gray-600">
                  Each tenant gets their own dedicated database instance
                </div>
                <div className="text-xs text-blue-600 mt-1">✓ Maximum isolation, better performance</div>
              </div>
            </label>

            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="databaseMode"
                value="hybrid"
                checked={systemConfig.databaseMode === 'hybrid'}
                onChange={(e) => setSystemConfig({...systemConfig, databaseMode: e.target.value})}
                className="mt-0.5"
              />
              <div>
                <div className="font-medium">Hybrid Strategy</div>
                <div className="text-sm text-gray-600">
                  Enterprise tenants get dedicated databases, others share
                </div>
                <div className="text-xs text-purple-600 mt-1">✓ Balanced approach, flexible scaling</div>
              </div>
            </label>
          </div>
        </div>

        {/* Connection Pooling */}
        <div className="border-t pt-6">
          <h3 className="font-medium text-gray-900 mb-4">Connection Management</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Connections Per Tenant
              </label>
              <input
                type="number"
                defaultValue="50"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                min="1"
                max="1000"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Connection Timeout (seconds)
              </label>
              <input
                type="number"
                defaultValue="30"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                min="1"
                max="300"
              />
            </div>
          </div>
        </div>

        {/* Backup Configuration */}
        <div className="border-t pt-6">
          <h3 className="font-medium text-gray-900 mb-4">Backup & Recovery</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Backup Frequency
                </label>
                <select className="w-full border border-gray-300 rounded-lg px-3 py-2">
                  <option value="daily">Daily</option>
                  <option value="hourly">Hourly</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Retention Period
                </label>
                <select className="w-full border border-gray-300 rounded-lg px-3 py-2">
                  <option value="7">7 days</option>
                  <option value="30">30 days</option>
                  <option value="90">90 days</option>
                  <option value="365">1 year</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Backup Storage
                </label>
                <select className="w-full border border-gray-300 rounded-lg px-3 py-2">
                  <option value="local">Local Storage</option>
                  <option value="s3">Amazon S3</option>
                  <option value="gcs">Google Cloud Storage</option>
                </select>
              </div>
            </div>

            <div className="flex space-x-4">
              <label className="flex items-center space-x-2">
                <input type="checkbox" checked className="rounded" />
                <span className="text-sm">Automated backups</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" checked className="rounded" />
                <span className="text-sm">Per-tenant backup isolation</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="rounded" />
                <span className="text-sm">Point-in-time recovery</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default {
  BrandingDomains,
  GlobalIntegrations,
  SecurityPrivacy,
  DatabaseConfiguration
};